import { useCallback, useEffect, useState } from "react";
import {
  getGlobalSettings,
  updateGlobalSettings,
  type GlobalSettings,
  type GlobalSettingsUpdate,
} from "../api/admin";

// Tüm müşterilerde ortak AI sağlayıcı ayarları (API key / model / bütçe).
// Müşteri panelinde yalnız "varsayılan sağlayıcı" seçimi vardır; key'ler buradan.

// MODELS — sağlayıcı başına SEÇİLEBİLİR model listesi.
//
// 2026-08-04'te her sağlayıcının kendi model API'sinden doğrulandı; kaldırılmış
// veya cevap vermeyen modeller listeden ÇIKARILDI ki admin yanlışlıkla seçmesin:
//   · gemini-2.0-pro    → 404. Üretimde SEÇİLİ olduğu için Gemini her çağrıda
//                          anında hata dönüyordu.
//   · gemini-2.0-flash  → ListModels çıktısında VAR ama generateContent 404
//                          veriyor (2026-08-05'te tek tek denendi). Listelenmek
//                          çalışmak demek değil; bu yüzden liste artık canlı
//                          ÇAĞRIYLA doğrulanıyor, sadece ListModels'a bakılmıyor.
// Kalan altısı 200 döndürdü ve listede o yüzden duruyor.
//   · deepseek-reasoner → DeepSeek'in /models çıktısında YOK.
//
// deepseek-v4-flash GERİ EKLENDİ (2026-08-05). 2026-08-04'te gövde hiç
// döndürmüyordu (300 sn'de bile) ve listeden çıkarılmıştı; ertesi gün yapılan
// ölçümde 4/4 başarılı, 2,3-2,6 sn — DeepSeek tarafındaki arıza geçiciymiş.
// Flash pro'dan ~3 kat hızlı, o yüzden seçilebilir kalmalı. Yine de VARSAYILAN
// pro: flash saatlerce kesintiye uğradı, pro o süre boyunca ayaktaydı.
const MODELS = {
  openai: ["gpt-4o-mini", "gpt-4o", "gpt-4.1-mini", "gpt-4.1", "gpt-5.4-mini", "gpt-5.4", "gpt-5.5"],
  gemini: ["gemini-2.5-flash", "gemini-2.5-pro", "gemini-3.5-flash", "gemini-3.6-flash", "gemini-flash-latest", "gemini-pro-latest"],
  deepseek: ["deepseek-v4-pro", "deepseek-v4-flash"],
} as const;
type ProviderKey = keyof typeof MODELS;

export default function AISettings() {
  const [s, setS] = useState<GlobalSettings | null>(null);
  const [draft, setDraft] = useState<GlobalSettingsUpdate>({});
  const [tab, setTab] = useState<"ai" | "pricing">("ai");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const cur = await getGlobalSettings();
      setS(cur);
      setDraft({
        openai_text_model: cur.openai_text_model,
        openai_daily_usd: cur.openai_daily_usd,
        openai_monthly_usd: cur.openai_monthly_usd,
        openai_api_key: "",
        gemini_text_model: cur.gemini_text_model,
        gemini_daily_usd: cur.gemini_daily_usd,
        gemini_monthly_usd: cur.gemini_monthly_usd,
        gemini_api_key: "",
        deepseek_text_model: cur.deepseek_text_model,
        deepseek_daily_usd: cur.deepseek_daily_usd,
        deepseek_monthly_usd: cur.deepseek_monthly_usd,
        deepseek_api_key: "",
        default_ai_provider: cur.default_ai_provider,
        rag_provider: cur.rag_provider,
        rag_model: cur.rag_model,
        gspr_provider: cur.gspr_provider,
        gspr_model: cur.gspr_model,
        fallback_provider: cur.fallback_provider,
        fallback_model: cur.fallback_model,
        voyage_api_key: "",
        doc_price_usd: cur.doc_price_usd,
        usd_try_rate: cur.usd_try_rate,
        usd_eur_rate: cur.usd_eur_rate,
        price_gspr_generation_credits: cur.price_gspr_generation_credits,
        price_gspr_audit_credits: cur.price_gspr_audit_credits,
        price_report_edit_credits: cur.price_report_edit_credits,
        price_doc_analysis_credits: cur.price_doc_analysis_credits,
        price_doc_edit_credits: cur.price_doc_edit_credits,
        price_translation_credits: cur.price_translation_credits,
        doc_analysis_active: cur.doc_analysis_active,
        translation_active: cur.translation_active,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setErr(null);
    setOkMsg(null);
    try {
      const next = await updateGlobalSettings(draft);
      setS(next);
      setDraft((d) => ({ ...d, openai_api_key: "", gemini_api_key: "", deepseek_api_key: "", voyage_api_key: "" }));
      setOkMsg("Ayarlar kaydedildi.");
      setTimeout(() => setOkMsg(null), 4000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft]);

  if (loading && !s) return <div className="page"><p className="muted">Yükleniyor…</p></div>;

  return (
    <div className="page">
      <div className="page-head">
        <h1>AI &amp; Fiyatlandırma</h1>
        <p className="page-sub">Sağlayıcı anahtarları / model / bütçe ve kredi bazlı fiyatlandırma.</p>
      </div>

      <div className="tabs">
        <button className={"tab" + (tab === "ai" ? " active" : "")} onClick={() => setTab("ai")}>AI</button>
        <button className={"tab" + (tab === "pricing" ? " active" : "")} onClick={() => setTab("pricing")}>Fiyatlandırma</button>
      </div>

      {err && <div className="error">{err}</div>}
      {okMsg && <div className="notice">{okMsg}</div>}

      {tab === "ai" && (<>
      <p className="muted" style={{ marginBottom: 16 }}>
        Bu anahtarlar ve aşağıdaki sağlayıcı seçimi <strong>tüm müşterilerde ortaktır</strong>.
        Müşteriler ne anahtar girer ne sağlayıcı seçer — seçim yalnızca buradan yapılır.
      </p>

      <h2 className="section-title">Varsayılan AI sağlayıcısı</h2>
      {/* İŞ YÜKÜNE GÖRE AYRI SEÇİM (2026-08-04). Tek varsayılan yetmiyordu:
          GSPR bir dokümanda 142 çağrı yapıyor, Mevzuat Analizi tek çağrı.
          2026-08-05 ölçümü (gerçek prompt, 8'er tekrar): deepseek-v4-flash ile
          gpt-5.5 kalitede denk; flash tipik sürede daha hızlı ve ~40 kat ucuz.
          Bu yüzden ikisi de DeepSeek, kesinti riskini yedek sağlayıcı karşılıyor. */}
      <div className="card">
        {/* 2 sütun SABİT: sağlayıcı ve modeli aynı satırda kalsın. Otomatik
            sarmada ikisi ayrı satırlara düşüp hangi modelin hangi sağlayıcıya
            ait olduğu okunmaz hale geliyordu. */}
        <div className="cf-grid" style={{ alignItems: "end", gridTemplateColumns: "repeat(2, minmax(220px, 1fr))" }}>
          <label className="cf-field">
            <span>Mevzuat Analizi — sağlayıcı <em>(tek çağrı; serbest AI yanıtı da bunu kullanır)</em></span>
            <select
              value={draft.rag_provider || ""}
              onChange={(e) => setDraft((d) => ({ ...d, rag_provider: e.target.value }))}
            >
              <option value="">(varsayılan: DeepSeek)</option>
              <option value="deepseek">DeepSeek</option>
              <option value="openai">OpenAI</option>
              <option value="gemini">Google Gemini</option>
            </select>
          </label>
          <label className="cf-field">
            <span>Mevzuat Analizi — model</span>
            <select
              value={draft.rag_model || ""}
              onChange={(e) => setDraft((d) => ({ ...d, rag_model: e.target.value }))}
            >
              <option value="">(sağlayıcının kendi modeli)</option>
              {MODELS[(draft.rag_provider || "deepseek") as ProviderKey].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
          <label className="cf-field">
            <span>GSPR üretimi — sağlayıcı <em>(142 çağrı; hız ve maliyet belirleyici)</em></span>
            <select
              value={draft.gspr_provider || ""}
              onChange={(e) => setDraft((d) => ({ ...d, gspr_provider: e.target.value }))}
            >
              <option value="">(varsayılan: DeepSeek)</option>
              <option value="deepseek">DeepSeek</option>
              <option value="openai">OpenAI</option>
              <option value="gemini">Google Gemini</option>
            </select>
          </label>
          <label className="cf-field">
            <span>GSPR üretimi — model</span>
            <select
              value={draft.gspr_model || ""}
              onChange={(e) => setDraft((d) => ({ ...d, gspr_model: e.target.value }))}
            >
              <option value="">(sağlayıcının kendi modeli)</option>
              {MODELS[(draft.gspr_provider || "deepseek") as ProviderKey].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
        </div>
        {/* YEDEK SAĞLAYICI — birincil cevap veremezse otomatik devreye girer.
            2026-08-04'te birincil saatlerce sessizce asıldı ve TÜM AI özellikleri
            durdu; bu alan o senaryonun karşılığı. */}
        <div className="cf-grid" style={{ alignItems: "end", gridTemplateColumns: "repeat(2, minmax(220px, 1fr))", marginTop: 14, paddingTop: 14, borderTop: "1px dashed var(--border)" }}>
          <label className="cf-field">
            <span>Yedek sağlayıcı <em>(birincil cevap vermezse devreye girer)</em></span>
            <select
              value={draft.fallback_provider || ""}
              onChange={(e) => setDraft((d) => ({ ...d, fallback_provider: e.target.value }))}
            >
              <option value="">(varsayılan: OpenAI)</option>
              <option value="openai">OpenAI</option>
              <option value="deepseek">DeepSeek</option>
              <option value="gemini">Google Gemini</option>
            </select>
          </label>
          <label className="cf-field">
            <span>Yedek model</span>
            <select
              value={draft.fallback_model || ""}
              onChange={(e) => setDraft((d) => ({ ...d, fallback_model: e.target.value }))}
            >
              <option value="">(varsayılan: gpt-5.5)</option>
              {MODELS[(draft.fallback_provider || "openai") as ProviderKey].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
        </div>
        <p className="muted" style={{ margin: "8px 0 0", fontSize: 12.5 }}>
          Birincil sağlayıcı hata verir veya <strong>45 saniye içinde cevap vermezse</strong> istek
          otomatik olarak yedeğe gider. Bütçe limiti aşıldığında yedeğe DÜŞÜLMEZ — limit kasıtlıdır.
          Yedek boş bırakılırsa birincil düştüğünde istek hata döner.
        </p>
        <p className="muted" style={{ margin: "10px 0 0", fontSize: 12.5 }}>
          <strong>Mevzuat Analizi</strong> kutusu ve serbest AI yanıtı ilk seçimi;
          <strong> GSPR üretimi, denetçi ve DOCX</strong> ikinci seçimi kullanır.
          Model boş bırakılırsa sağlayıcı kartında seçili model geçerlidir.
          Seçilen sağlayıcının API anahtarı aşağıda girili olmalıdır.
        </p>
      </div>

      <ProviderCard
        title="OpenAI"
        keySet={s?.openai_api_key_set}
        keyPlaceholder="sk-..."
        models={[...MODELS.openai]}
        model={draft.openai_text_model || ""}
        apiKey={draft.openai_api_key || ""}
        daily={draft.openai_daily_usd ?? 0}
        monthly={draft.openai_monthly_usd ?? 0}
        onModel={(v) => setDraft((d) => ({ ...d, openai_text_model: v }))}
        onKey={(v) => setDraft((d) => ({ ...d, openai_api_key: v }))}
        onDaily={(v) => setDraft((d) => ({ ...d, openai_daily_usd: v }))}
        onMonthly={(v) => setDraft((d) => ({ ...d, openai_monthly_usd: v }))}
      />

      <ProviderCard
        title="Google Gemini"
        keySet={s?.gemini_api_key_set}
        keyPlaceholder="AIza..."
        models={[...MODELS.gemini]}
        model={draft.gemini_text_model || ""}
        apiKey={draft.gemini_api_key || ""}
        daily={draft.gemini_daily_usd ?? 0}
        monthly={draft.gemini_monthly_usd ?? 0}
        onModel={(v) => setDraft((d) => ({ ...d, gemini_text_model: v }))}
        onKey={(v) => setDraft((d) => ({ ...d, gemini_api_key: v }))}
        onDaily={(v) => setDraft((d) => ({ ...d, gemini_daily_usd: v }))}
        onMonthly={(v) => setDraft((d) => ({ ...d, gemini_monthly_usd: v }))}
      />

      <ProviderCard
        title="DeepSeek"
        keySet={s?.deepseek_api_key_set}
        keyPlaceholder="sk-..."
        /* deepseek-chat KALDIRILDI: DeepSeek bu modeli yayından çekti (2026-07).
           deepseek-v4-pro ÖNCE gelir çünkü ölçülen tek çalışan model odur.

           2026-08-04 ölçümü (üretim anahtarıyla, hem sunucudan hem dışarıdan):
             deepseek-v4-pro   → HTTP 200, 16–33 sn, gerçek cevap
             deepseek-v4-flash → HTTP 200 başlığı geliyor ama GÖVDE HİÇ GELMİYOR;
                                 300 sn'de bile bitmiyor. Log'daki karşılığı:
                                 "yanit govdesi okunamadi (status=200)".
           Hesap/anahtar/kota sağlam (/user/balance ve /models 0,3 sn'de 200);
           arıza DeepSeek'in flash modelinde. Sorun kalkarsa flash geri seçilebilir.

           v4-pro bir AKIL YÜRÜTME modelidir: cevabın bir kısmı reasoning
           token'ına gider, bu yüzden max_tokens küçükse içerik BOŞ döner. */
        models={[...MODELS.deepseek]}
        model={draft.deepseek_text_model || ""}
        apiKey={draft.deepseek_api_key || ""}
        daily={draft.deepseek_daily_usd ?? 0}
        monthly={draft.deepseek_monthly_usd ?? 0}
        onModel={(v) => setDraft((d) => ({ ...d, deepseek_text_model: v }))}
        onKey={(v) => setDraft((d) => ({ ...d, deepseek_api_key: v }))}
        onDaily={(v) => setDraft((d) => ({ ...d, deepseek_daily_usd: v }))}
        onMonthly={(v) => setDraft((d) => ({ ...d, deepseek_monthly_usd: v }))}
      />

      <h2 className="section-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>Voyage (embedding)</span>
        {s?.voyage_api_key_set && <span className="badge ok">anahtar girili</span>}
      </h2>
      <div className="card">
        <ApiKeyField
          keySet={s?.voyage_api_key_set}
          value={draft.voyage_api_key || ""}
          onChange={(v) => setDraft((d) => ({ ...d, voyage_api_key: v }))}
          placeholder="pa-..."
        />
        <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
          Anlamsal arama (Mevzuat Analizi) bu anahtarı kullanır. Boş bırakılırsa sistem
          yalnız sözcük aramasıyla çalışır — hata vermez, isabet düşer.
        </p>
      </div>
      </>)}

      {tab === "pricing" && (<>
      <h2 className="section-title">Ödeme / Kur</h2>
      <div className="card">
        <div className="cf-grid" style={{ maxWidth: 560, alignItems: "end" }}>
          <label className="cf-field">
            <span>1 Kredi Fiyatı (USD) <em>— kredi satın alma birim fiyatı (ör. 1)</em></span>
            <input
              type="number" step="0.01" min="0"
              value={draft.doc_price_usd ?? 0}
              onChange={(e) => setDraft((d) => ({ ...d, doc_price_usd: parseFloat(e.target.value) || 0 }))}
            />
          </label>
          <label className="cf-field">
            <span>USD/TRY kuru <em>— birkaç saatte bir otomatik güncellenir</em></span>
            <input
              type="number" step="0.01" min="0"
              value={draft.usd_try_rate ?? 0}
              onChange={(e) => setDraft((d) => ({ ...d, usd_try_rate: parseFloat(e.target.value) || 0 }))}
            />
          </label>
          <label className="cf-field">
            <span>USD/EUR kuru <em>— 1 USD kaç EUR; aynı serviste otomatik güncellenir</em></span>
            <input
              type="number" step="0.0001" min="0"
              value={draft.usd_eur_rate ?? 0}
              onChange={(e) => setDraft((d) => ({ ...d, usd_eur_rate: parseFloat(e.target.value) || 0 }))}
            />
          </label>
        </div>
        <p className="muted" style={{ margin: "10px 0 0", fontSize: 12.5 }}>
          Garanti TL (949) tahsil eder. 1 kredi müşteriye <strong>{((draft.doc_price_usd ?? 0) * (draft.usd_try_rate ?? 0)).toFixed(2)} TL</strong>
          {" "}yansır (= {(draft.doc_price_usd ?? 0).toFixed(2)} USD × {(draft.usd_try_rate ?? 0).toFixed(2)}).
          {" "}Kredi başına USD fiyatı budur; işlemlerin kaç krediye mal olduğu aşağıda tanımlanır. Kur otomatik tazelenir, elle de değiştirebilirsiniz.
        </p>
        <p className="muted" style={{ margin: "6px 0 0", fontSize: 12.5 }}>
          <strong>USD/EUR yalnız havale içindir.</strong> Kart ve PayPal tahsilatında kullanılmaz
          (kart TL, PayPal USD çeker). Banka hesaplarımızda EUR hesabı da olduğu için
          müşteri panelde <strong>{((draft.doc_price_usd ?? 0) * (draft.usd_eur_rate ?? 0)).toFixed(2)} EUR</strong>
          {" "}birim fiyatını görür ve kaç EUR göndereceğini bilir. Kur 0 ise EUR tutarı panelde gösterilmez.
        </p>
      </div>

      <h2 className="section-title">Özellik Fiyatlandırması (Kredi)</h2>
      <div className="card">
        <div className="cf-grid" style={{ maxWidth: 720, alignItems: "end" }}>
          <label className="cf-field">
            <span>GSPR Üretimi <em>— işlem başına kredi (Dokümanı Oluştur'da düşer)</em></span>
            <input
              type="number" step="1" min="1"
              value={draft.price_gspr_generation_credits ?? 0}
              onChange={(e) => setDraft((d) => ({ ...d, price_gspr_generation_credits: parseInt(e.target.value, 10) || 0 }))}
            />
          </label>
          <label className="cf-field">
            <span>GSPR–Denetçi Karşılaştırmalı Analiz <em>— işlem başına kredi</em></span>
            <input
              type="number" step="1" min="0"
              value={draft.price_gspr_audit_credits ?? 0}
              onChange={(e) => setDraft((d) => ({ ...d, price_gspr_audit_credits: parseInt(e.target.value, 10) || 0 }))}
            />
          </label>
          <label className="cf-field">
            <span>Rapora Göre Rapor Düzenleme <em>— işlem başına kredi</em></span>
            <input
              type="number" step="1" min="0"
              value={draft.price_report_edit_credits ?? 0}
              onChange={(e) => setDraft((d) => ({ ...d, price_report_edit_credits: parseInt(e.target.value, 10) || 0 }))}
            />
          </label>
          <label className="cf-field">
            <span>Doküman Analizi <em>— işlem başına kredi (aktifse Analiz'de düşer)</em></span>
            <input
              type="number" step="1" min="1"
              value={draft.price_doc_analysis_credits ?? 0}
              onChange={(e) => setDraft((d) => ({ ...d, price_doc_analysis_credits: parseInt(e.target.value, 10) || 0 }))}
            />
          </label>
          <label className="cf-field">
            <span>Analize Göre Doküman Düzenleme <em>— işlem başına kredi (gelecek faz)</em></span>
            <input
              type="number" step="1" min="0"
              value={draft.price_doc_edit_credits ?? 0}
              onChange={(e) => setDraft((d) => ({ ...d, price_doc_edit_credits: parseInt(e.target.value, 10) || 0 }))}
            />
          </label>
          <label className="cf-field">
            <span>Doküman Çevirisi <em>— doküman başına kredi</em></span>
            <input
              type="number" step="1" min="0"
              value={draft.price_translation_credits ?? 0}
              onChange={(e) => setDraft((d) => ({ ...d, price_translation_credits: parseInt(e.target.value, 10) || 0 }))}
            />
          </label>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "14px 0 0", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!!draft.doc_analysis_active}
            onChange={(e) => setDraft((d) => ({ ...d, doc_analysis_active: e.target.checked }))}
          />
          <span><strong>Doküman Analizi aktif</strong> — açıkken müşteri panelinde analiz + "analize göre düzenle" çalışır ve krediler düşülür. Kapalıyken pasiftir (kredi düşmez).</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0 0", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!!draft.translation_active}
            onChange={(e) => setDraft((d) => ({ ...d, translation_active: e.target.checked }))}
          />
          <span><strong>Doküman Çevirisi aktif</strong> — açıkken müşteri panelinde çeviri çalışır ve doküman başına kredi düşülür. Kapalıyken pasiftir.</span>
        </label>
        <p className="muted" style={{ margin: "10px 0 0", fontSize: 12.5 }}>
          <strong>GSPR Üretimi</strong> "Dokümanı Oluştur" işleminde düşülür (aktif). <strong>Doküman Analizi</strong>
          {" "}yukarıdaki bayrakla aktifleştirilir (250 varsayılan). <strong>Analize Göre Doküman Düzenleme</strong> (500)
          {" "}ve <strong>Doküman Çevirisi</strong> (100, doküman başına) fiyatları tanımlı; ilgili akışlar
          aktifleştikçe devreye girer. GSPR–Denetçi Analiz ve Rapor Düzenleme henüz kredi düşümüne bağlı değildir.
        </p>
      </div>
      </>)}

      <div className="detail-actions">
        <button className="btn-soft" disabled={saving} onClick={load}>Sıfırla</button>
        <button disabled={saving} onClick={onSave} style={{
          padding: "8px 18px", borderRadius: 8, border: "none",
          background: "var(--accent)", color: "#fff", fontWeight: 600, cursor: "pointer",
        }}>
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

// ApiKeyField — API anahtarı girişi. ProviderCard ve Voyage bölümü AYNI
// bileşeni kullanır; iki yerde ayrı kopya tutmak, birinde yapılan düzeltmenin
// diğerine geçmemesi demekti.
//
// Kayıtlı anahtar sunucudan ASLA geri gelmez (GET'te gizlenir, yalnız "girili mi"
// bilgisi döner). O yüzden dolu durumda kutuya gerçek değer değil sabit uzunlukta
// bir maske yazılır — boş kutu göstermek admin'i "anahtar kaybolmuş" sanmaya
// itiyordu. Göz düğmesi o durumda pasiftir: gösterilecek bir şey yok.
//
// Varsayılan GİZLİ: admin ekranı paylaşırken/ekran görüntüsü alırken anahtar sızmasın.
function ApiKeyField(props: {
  keySet?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  const [reveal, setReveal] = useState(false);
  const masked = "•".repeat(24);
  const showMask = props.keySet && props.value === "";
  return (
    <label className="cf-field" style={{ marginBottom: 14 }}>
      <span>API Anahtarı {props.keySet && <em>✓ girili — değiştirmek için üzerine yazın</em>}</span>
      <span style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
        <input
          style={{ flex: 1 }}
          type={reveal && !showMask ? "text" : "password"}
          value={showMask ? masked : props.value}
          onChange={(e) => props.onChange(e.target.value)}
          onFocus={() => { if (showMask) props.onChange(""); }}
          placeholder={props.keySet ? "" : props.placeholder}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          className="btn-sec"
          onClick={() => setReveal((v) => !v)}
          disabled={showMask}
          title={showMask ? "Kayıtlı anahtar sunucudan geri okunamaz" : (reveal ? "Gizle" : "Göster")}
          aria-label={reveal ? "Anahtarı gizle" : "Anahtarı göster"}
          style={{ minWidth: 44 }}
        >
          {reveal && !showMask ? "🙈" : "👁"}
        </button>
      </span>
      {showMask && (
        <em className="muted" style={{ fontSize: 12 }}>
          Kayıtlı anahtar güvenlik gereği geri okunamaz; yeni bir anahtar yazarsanız değişir.
        </em>
      )}
    </label>
  );
}

function ProviderCard(props: {
  title: string;
  keySet?: boolean;
  keyPlaceholder: string;
  models: string[];
  model: string;
  apiKey: string;
  daily: number;
  monthly: number;
  onModel: (v: string) => void;
  onKey: (v: string) => void;
  onDaily: (v: number) => void;
  onMonthly: (v: number) => void;
}) {
  // Anahtar alanının kendisi ApiKeyField'da (Voyage bölümüyle ORTAK).
  return (
    <>
      <h2 className="section-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{props.title}</span>
        {props.keySet && <span className="badge ok">anahtar girili</span>}
      </h2>
      <div className="card">
        {/* API anahtarı ÜSTTE, tam genişlikte tek satır (müşteri kararı
            2026-08-04). Altında model + bütçeler yan yana. */}
        <ApiKeyField
          keySet={props.keySet}
          value={props.apiKey}
          onChange={props.onKey}
          placeholder={props.keyPlaceholder}
        />

        <div className="cf-grid">
          <label className="cf-field">
            <span>Model</span>
            {/* Kayıtlı model listede yoksa BAŞA EKLENİR. Aksi halde select boş
                görünüyor (React value hiçbir option'la eşleşmiyor) ve admin
                farkında olmadan listedeki ilk modele geçebiliyor. Model .env
                veya API üzerinden de set edilebildiği için liste her zaman
                eksik olabilir — 2026-07-30'da DeepSeek tam bu yüzden boş
                görünüyordu (kayıtlı deepseek-v4-flash, liste deepseek-chat). */}
            <select value={props.model} onChange={(e) => props.onModel(e.target.value)}>
              <option value="">(varsayılan)</option>
              {(props.model && !props.models.includes(props.model)
                ? [props.model, ...props.models]
                : props.models
              ).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="cf-field">
            <span>Günlük bütçe (USD)</span>
            <input type="number" step="0.5" min="0" value={props.daily}
              onChange={(e) => props.onDaily(parseFloat(e.target.value) || 0)} />
          </label>
          <label className="cf-field">
            <span>Aylık bütçe (USD)</span>
            <input type="number" step="5" min="0" value={props.monthly}
              onChange={(e) => props.onMonthly(parseFloat(e.target.value) || 0)} />
          </label>
        </div>
      </div>
    </>
  );
}
